import { CoreButton } from '@components'
import TrashIcon from '@images/icons/trash-modal.svg?react'
import { toastFailure, toastSuccess } from '@utils'
import { useDeactivatePaymentAccountMutation, useDeleteExternalInstrumentsMutation } from 'mmfintech-backend-api'
import { GlobalContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext } from 'react'

import './deleteElementModal.scss'

export const DeleteElementModal = ({
  element,
  elementType = 'account',
  onDelete
}: {
  element?: any
  elementType?: 'account' | 'recipient'
  onDelete?: () => void
}) => {
  const { modalHide } = useContext(GlobalContext)
  const [deactivateAccount, { error: editAccountError, isLoading: editAccountFetching }] =
    useDeactivatePaymentAccountMutation()
  const [deleteRecipients, { error: deleteRecipientsError, isLoading: deleteRecipientsFetching }] =
    useDeleteExternalInstrumentsMutation()

  const handleSuccessfulChange = () => {
    if (elementType === 'account') {
      toastSuccess(tr('FRONTEND.ACCOUNTS.DELETE.SUCCESS', 'Account successfully deleted'), undefined, {
        duration: 3_000
      })
    } else {
      toastSuccess(tr('FRONTEND.RECIPIENT.DELETE.SUCCESS', 'Recipient successfully deleted'), undefined, {
        duration: 3_000
      })
    }
    onDelete?.()
    modalHide()
  }

  const handleSubmit = async () => {
    let response: { data?: unknown; error?: unknown }
    if (elementType === 'account') {
      response = await deactivateAccount({ id: element?.id })
    } else {
      response = await deleteRecipients({
        instrumentIds: [element?.paymentInstrumentId]
      })
    }
    if ('error' in response) {
      const { error }: any = response || {}
      const { errorKey, error: textError } = error || {}
      toastFailure(tr(errorKey, textError), undefined, { duration: 3_000 })
      return
    }
    handleSuccessfulChange()
  }

  return (
    <div className='delete-element-modal-wrapper'>
      <div className='delete-element-icon'>
        <TrashIcon />
      </div>
      <div className='delete-element-message'>
        {elementType === 'account'
          ? tr(
              'FRONTEND.ACCOUNTS.DELETE.MODAL_CONTENT',
              'This will prevent any future operations from and to the account you want to close. Do you want to proceed?'
            )
          : tr(
              'FRONTEND.RECIPIENT.DELETE.MODAL_CONTENT',
              'This will permanently delete selected recipient. Do you want to proceed?'
            )}
      </div>

      <ErrorDisplay error={editAccountError || deleteRecipientsError} />

      <div className='delete-element-actions'>
        <CoreButton
          variation='primary-black'
          text={
            elementType === 'account'
              ? tr('FRONTEND.ACCOUNTS.BUTTON.CLOSE_ACCOUNT', 'Close account')
              : tr('FRONTEND.BUTTONS.DELETE', 'Delete')
          }
          size='large'
          fullWidth
          isLoading={editAccountFetching || deleteRecipientsFetching}
          onClick={handleSubmit}
        />
        <CoreButton
          variation='tertiary'
          text={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          size='large'
          onClick={modalHide}
        />
      </div>
    </div>
  )
}
